/**
 * Year fields on work experience and schools.
 *
 * `JobPosition` and `School` declare `startYear` / `endYear` as numbers, and the Typesense
 * `community_members` index types `user.jobs.startYear|endYear` and
 * `user.universities.startYear|endYear` as `int64[]`. Typesense validates the *whole* document:
 * one string year makes it reject the entire record with HTTP 400, so the member silently
 * disappears from the community member list with no symptom anywhere in the product — the only
 * trace is a failed Cloud Function execution. One member sat invisible for ~4 months that way.
 *
 * A DOM input's `.value` is a string even when `type="number"`, and LLM tool calls are not
 * type-checked either, so every write path has to coerce. Keep this the single implementation.
 */

/** Job keys the index typed as `bool[]`. They are not part of JobPosition, but the auto-detected
 *  schema constrains them for every future document, so non-booleans must not reach it. */
export const INDEXED_JOB_BOOLEAN_KEYS = ['cv', 'motivation', 'premium', 'published'] as const

/** Coerce a year to a number, or undefined when it cannot be read as one. */
export function toProfileYear(value: unknown): number | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) return value

    if (typeof value === 'string') {
        const parsed = Number.parseInt(value, 10)
        if (Number.isFinite(parsed)) return parsed
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.getFullYear()
    }

    if (value && typeof value === 'object') {
        const timestampLike = value as { toDate?: () => Date }
        if (typeof timestampLike.toDate === 'function') {
            const dateValue = timestampLike.toDate()
            if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
                return dateValue.getFullYear()
            }
        }
    }

    return undefined
}

/**
 * Make an array of jobs/schools safe to index: numeric years, and no non-boolean values on the
 * keys the index typed as booleans. Entries are only rewritten when something actually changes,
 * so callers can compare by reference to detect a no-op.
 */
export function normalizeIndexedProfileEntries<T extends object>(entries: T[] | undefined): T[] | undefined {
    if (!Array.isArray(entries)) return entries

    return entries.map((entry) => {
        if (!entry || typeof entry !== 'object') return entry

        const next = { ...entry } as Record<string, unknown>
        let changed = false

        for (const key of ['startYear', 'endYear'] as const) {
            if (!(key in next)) continue
            const raw = next[key]
            if (raw === undefined || raw === null || raw === '') continue
            const coerced = toProfileYear(raw)
            if (coerced !== raw) {
                next[key] = coerced
                changed = true
            }
        }

        for (const key of INDEXED_JOB_BOOLEAN_KEYS) {
            if (key in next && next[key] !== undefined && typeof next[key] !== 'boolean') {
                delete next[key]
                changed = true
            }
        }

        return (changed ? next : entry) as T
    })
}
